import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';

import { Table, TableContainer, TableHead, TableBody, Paper, Tooltip, IconButton, TableSortLabel, CircularProgress, Box } from '@mui/material';
import { Refresh } from '@mui/icons-material';

import { D, TRow, TCell } from '../include';

moment.locale('pt_br');

export const LastLogins = ({ api, user, refreshInterval }) => {
  const [dataTable, setDataTable] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('last_login');
  const [now, setNow] = useState(moment());
  const [loading, setLoading] = useState(false);

  // useRef para controlar o "já logou" na inicialização estrita do React 18
  const hasLogged = useRef(false);

  // =====================================
  // BUSCA DE DADOS (FETCH)
  // =====================================
  const updateData = useCallback((logar = false) => {
    setLoading(true);

    api.get('/lastlogins', { params: { logar } })
      .then(r => {
        const rows = r && r.logs ? r.logs : [];
        const safeRows = Array.isArray(rows) ? rows : [];

        // aplica filtro "ai2bizlab" se não for admin
        const filtered = (user && user.role !== 'admin')
          ? safeRows.filter(x => !(x.username || '').toLowerCase().includes('ai2bizlab'))
          : safeRows;

        setDataTable(filtered);
        setNow(moment());
      })
      .catch(err => {
        console.error('Erro ao buscar /lastlogins:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [api, user]);

  // =====================================
  // CONTROLE DE CICLO DE VIDA (TIMERS)
  // =====================================
  useEffect(() => {
    // Apenas registra o log (true) na primeira vez
    const shouldLog = !hasLogged.current;
    hasLogged.current = true;
    updateData(shouldLog);

    const pollMs = Number(refreshInterval) || 30000;
    const clockMs = 15000;

    // Timer para buscar novos dados na API
    const pollInterval = setInterval(() => {
      updateData(false);
    }, pollMs);

    // Timer para atualizar o relógio visual de "há X minutos"
    const clockInterval = setInterval(() => {
      setNow(moment());
    }, clockMs);

    // Limpeza automática ao desmontar
    return () => {
      clearInterval(pollInterval);
      clearInterval(clockInterval);
    };
  }, [updateData, refreshInterval]);


  // =====================================
  // LÓGICA DE APRESENTAÇÃO (MEMOIZADA)
  // =====================================
  const formatElapsed = useCallback((lastLogin) => {
    if (!lastLogin) return '';

    const mLogin = moment(lastLogin);
    const diffSeconds = now.diff(mLogin, 'seconds');
    
    if (diffSeconds < 5) return 'agora mesmo';
    if (diffSeconds < 60) return `há ${diffSeconds}s`;
    
    const diffMinutes = now.diff(mLogin, 'minutes');
    if (diffMinutes < 60) return `há ${diffMinutes} min${diffMinutes > 1 ? 's' : ''}`;
    
    const diffHours = now.diff(mLogin, 'hours');
    if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    
    const diffDays = now.diff(mLogin, 'days');
    return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  }, [now]);


  const sortedRows = useMemo(() => {
    const rowsCopy = [...dataTable];

    if (orderBy === 'last_login') {
      const sorted = rowsCopy.sort((a, b) => {
        const aa = a.last_login || a.lastLogin || a.created_at;
        const bb = b.last_login || b.lastLogin || b.created_at;
        const da = aa ? new Date(aa).getTime() : 0;
        const db = bb ? new Date(bb).getTime() : 0;
        return da - db;
      });
      return order === 'asc' ? sorted : sorted.reverse();
    } else {
      const sorted = rowsCopy.sort((a, b) => {
        const ua = (a.username || '').toString();
        const ub = (b.username || '').toString();
        return ua.localeCompare(ub, 'pt-BR');
      });
      return order === 'asc' ? sorted : sorted.reverse();
    }
  }, [dataTable, order, orderBy]);


  const createSortHandler = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Botão manual reseta o timer implícitamente? 
  // No hook, se quisermos resetar o poll, a forma mais comum é forçar a montagem 
  // de novo, ou apenas aceitar o refresh assíncrono. Como é um botão manual, 
  // chamar updateData() é suficiente.
  const handleManualRefresh = () => {
    updateData(false);
  };


  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      <D className="row w aC" style={{ marginBottom: '1rem' }}>
        <Tooltip title="Atualizar lista" arrow>
          <IconButton onClick={handleManualRefresh}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </D>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow sx={{ height: '3rem', background: '#ccc' }}>
                <TCell sortDirection={orderBy === 'username' ? order : false} sx={{ color: 'white', width: '25%' }}>
                  <TableSortLabel
                    active={orderBy === 'username'}
                    direction={orderBy === 'username' ? order : 'asc'}
                    onClick={() => createSortHandler('username')}
                    sx={{
                      color: 'white',
                      '&.Mui-active': { color: 'white' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                      '&:hover': { color: 'white' },
                      '&:hover .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    Usuário
                  </TableSortLabel>
                </TCell>

                <TCell sortDirection={orderBy === 'last_login' ? order : false} sx={{ color: 'white', width: '15%' }}>
                  <TableSortLabel
                    active={orderBy === 'last_login'}
                    direction={orderBy === 'last_login' ? order : 'asc'}
                    onClick={() => createSortHandler('last_login')}
                    sx={{
                      color: 'white',
                      '&.Mui-active': { color: 'white' },
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                      '&:hover': { color: 'white' },
                      '&:hover .MuiTableSortLabel-icon': { color: 'white !important' }
                    }}
                  >
                    Último login
                  </TableSortLabel>
                </TCell>

                <TCell sx={{ color: 'white' }}>Tempo desde o login</TCell>
              </TRow>
            </TableHead>

            <TableBody>
              {loading && dataTable.length === 0 ? (
                // Mostra o spinner apenas se for a primeira carga ou não houver dados.
                // Isso evita que a tabela "pisque" e suma durante o polling automático.
                <TRow>
                  <TCell colSpan={3} align="center" style={{ padding: '2rem' }}>
                    <Box display="flex" justifyContent="center">
                      <CircularProgress size={32} />
                    </Box>
                  </TCell>
                </TRow>
              ) : sortedRows.length === 0 ? (
                <TRow>
                  <TCell component="th" align="center" colSpan={3}>
                    Nenhum usuário com sessão ativa
                  </TCell>
                </TRow>
              ) : (
                sortedRows.map((row, idx) => {
                  const lastLoginRaw = row.last_login || row.lastLogin || row.created_at;
                  const lastMoment = lastLoginRaw ? moment(lastLoginRaw) : null;
                  const lastLoginBr = lastMoment ? lastMoment.format('DD/MM/YYYY HH:mm:ss') : '';
                  const elapsed = lastMoment ? formatElapsed(lastMoment) : '';

                  return (
                    <TRow key={`${row.username}-${idx}`}>
                      <TCell component="th">{row.username}</TCell>
                      <TCell component="th">{lastLoginBr}</TCell>
                      <TCell component="th">{elapsed}</TCell>
                    </TRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};

export default LastLogins;